import { lazy, Suspense } from 'react'
import {
  Button,
  DEV_TOOLS_ENVIRONMENT_CONTAINER_ID,
  LazyLoadedDevTools,
  DevToolsPortal,
} from '@quickmd-llc/design-system'
import { Link } from 'react-router-dom'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { SANDBOX_PAGE_ROUTE } from '../../../utils/constants'
import env from '../../../env.json'
import { isDevToolsEnabled } from '../isDevToolsEnabled'
import { FlagsSection } from './FlagsSection'

/**
 * Lazily imported because it is the only thing here that reaches `src/dev-users.json` —
 * real lower-environment credentials. `LazyLoadedDevTools` defers its own panels, but a
 * static import of this child would still be evaluated eagerly, and `DevTools` is pulled
 * in by `App.tsx`, so the credentials landed in the entry chunk on every build,
 * production included. Verified against a real build: with a static import,
 * `build`'s `index-*.js` contained the default persona's email; with this, it does not.
 *
 * That is only half the guarantee, though: the chunk is still *emitted*, and a production
 * deploy would serve it publicly. The `quickmd:blank-dev-users-in-production` plugin in
 * `vite.config.ts` empties the JSON for production builds so nothing is there to serve.
 */
const DevToolsLogin = lazy(() => import('./DevToolsLogin').then((m) => ({ default: m.DevToolsLogin })))

type AdminProfileSlice = {
  accountSettings?: { adminProfile?: { AdminId?: string; TimeZone?: string } }
}

// Static environment data — no provider dependencies, safe for outermost rendering
const staticEnvironmentData = {
  Environment: env.name,
  'API URL': env.baseURL,
  App: 'admin',
}

/**
 * DevTools shell — renders outside error boundaries for crash resilience.
 * Console, network, storage, and environment panels survive app crashes.
 * Pass this to WebAppErrorShell's `devTools` prop.
 */
export function DevToolsShell() {
  return (
    <span className='tailwind-scoped'>
      <LazyLoadedDevTools isDevToolsEnabled={isDevToolsEnabled} environmentData={staticEnvironmentData}>
        <Suspense fallback={null}>
          <DevToolsLogin />
        </Suspense>
      </LazyLoadedDevTools>
    </span>
  )
}

/**
 * Provider-dependent DevTools content — portals into the DevTools App tab.
 * Rendered inside the app tree (with Router, Redux, etc.) so it has access to all providers.
 * If the app crashes, this content disappears but the core DevTools panels survive.
 */
export function DevToolsAppContent() {
  // The accountSettings reducer is legacy untyped JS, so we narrow the slice
  // shape locally here rather than relying on store-wide type inference.
  const adminProfile = useAppSelector((state) => (state as unknown as AdminProfileSlice).accountSettings?.adminProfile)

  if (!isDevToolsEnabled) return null

  return (
    <>
      <DevToolsPortal>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: '#666' }}>
            Admin ID: {adminProfile?.AdminId ?? 'Not logged in'}
            {adminProfile?.TimeZone ? ` | TZ: ${adminProfile.TimeZone}` : null}
          </div>
          <Link to={SANDBOX_PAGE_ROUTE} className='w-full'>
            <Button as='div' className='w-full'>
              Sandbox
            </Button>
          </Link>
        </div>
      </DevToolsPortal>

      {/* Flags sit with the environment rather than with admin's own debug widgets — which
          environment a flag came from is the first thing you check when one looks wrong. */}
      <DevToolsPortal specificId={DEV_TOOLS_ENVIRONMENT_CONTAINER_ID}>
        <FlagsSection />
      </DevToolsPortal>
    </>
  )
}
